/*
 * knobs-search-insights.js
 * ─────────────────────────────────────────────────────────────────────────
 * Shared Algolia Insights glue for knobs.co. Loaded globally (header) AFTER
 * the inline aa-queue bootstrap in layout/theme.liquid, so window.aa exists
 * on every page — including PDP, where most add-to-carts happen.
 *
 * Responsibilities:
 *   1. recordClick() — fire clickedObjectIDsAfterSearch AND remember the
 *      queryID that led to each objectID (variant id) in localStorage.
 *   2. store()       — remember a queryID->objectID link without firing a
 *      click (used where the surface already fires its own click event).
 *   3. A PASSIVE /cart/add.js fetch observer that, when a remembered product
 *      is added to cart, fires convertedObjectIDsAfterSearch — the event that
 *      gives search revenue attribution (and lets Dynamic Re-Ranking /
 *      Personalization learn). The observer never alters the cart response.
 *
 * objectID === Shopify variant id === /cart/add.js `id`, so attribution is a
 * direct match across the search page, collection InstantSearch, and PDP.
 */
(function () {
  'use strict';

  var INDEX = 'shopify_products';
  var STORE_KEY = 'knobs_qid_v1';
  var TTL_MS = 2 * 60 * 60 * 1000; // 2h: how long a click stays attributable to a cart-add
  var MAX = 60;                    // cap stored links

  function aaReady() { return typeof window.aa === 'function'; }

  function userToken() {
    try {
      if (window.aa && typeof window.aa.getUserToken === 'function') return window.aa.getUserToken();
    } catch (e) {}
    return undefined; // omitted → aa falls back to its cookie token
  }

  /* ───────── queryID store: { objectID: { q: queryID, t: ts } } ───────── */
  function readMap() {
    try { return JSON.parse(localStorage.getItem(STORE_KEY) || '{}') || {}; }
    catch (e) { return {}; }
  }
  function writeMap(m) { try { localStorage.setItem(STORE_KEY, JSON.stringify(m)); } catch (e) {} }
  function prune(m) {
    var now = Date.now(), keys = Object.keys(m);
    keys.forEach(function (k) { if (!m[k] || (now - m[k].t) > TTL_MS) delete m[k]; });
    keys = Object.keys(m);
    if (keys.length > MAX) {
      keys.sort(function (a, b) { return m[a].t - m[b].t; });
      keys.slice(0, keys.length - MAX).forEach(function (k) { delete m[k]; });
    }
    return m;
  }
  function store(objectID, queryID) {
    if (!objectID || !queryID) return;
    var m = prune(readMap());
    m[String(objectID)] = { q: String(queryID), t: Date.now() };
    writeMap(m);
  }
  function lookup(objectID) {
    var e = readMap()[String(objectID)];
    if (!e || (Date.now() - e.t) > TTL_MS) return null;
    return e.q;
  }

  /* ───────── click: fire event + remember queryID ───────── */
  function recordClick(opts) {
    opts = opts || {};
    if (!opts.objectID) return;
    store(opts.objectID, opts.queryID);
    if (!aaReady() || !opts.queryID) return;
    try {
      window.aa('clickedObjectIDsAfterSearch', {
        index: opts.index || INDEX,
        eventName: opts.eventName || 'Product Clicked',
        queryID: String(opts.queryID),
        objectIDs: [String(opts.objectID)],
        positions: [parseInt(opts.position, 10) || 1],
        userToken: userToken()
      });
    } catch (e) {}
  }

  /* ───────── conversion: fire once when a remembered product is added ───────── */
  function convert(objectID) {
    var qid = lookup(objectID);
    if (!qid || !aaReady()) return;
    try {
      window.aa('convertedObjectIDsAfterSearch', {
        index: INDEX,
        eventName: 'Added to Cart',
        queryID: qid,
        objectIDs: [String(objectID)],
        userToken: userToken()
      });
    } catch (e) {}
    // one-shot: drop the link so re-adds / page reloads don't double-count
    try { var m = readMap(); delete m[String(objectID)]; writeMap(m); } catch (e) {}
  }

  function idsFromBody(body) {
    var ids = [];
    try {
      if (!body) return ids;
      if (typeof body === 'string') {
        var s = body.trim();
        if (s.charAt(0) === '{') {
          var j = JSON.parse(s);
          if (j.id != null) ids.push(j.id);
          if (j.items && j.items.length) j.items.forEach(function (it) { if (it && it.id != null) ids.push(it.id); });
        } else {
          var p = new URLSearchParams(s);
          if (p.get('id')) ids.push(p.get('id'));
        }
      } else if (typeof FormData !== 'undefined' && body instanceof FormData) {
        if (body.get('id')) ids.push(body.get('id'));
      }
    } catch (e) {}
    return ids.map(String);
  }

  /* ───────── passive /cart/add.js observer (returns the ORIGINAL promise) ───────── */
  if (window.fetch && !window.fetch.__knobsWrapped) {
    var orig = window.fetch;
    var wrapped = function (input, init) {
      var p = orig.apply(this, arguments);
      try {
        var url = (typeof input === 'string') ? input : (input && input.url) || '';
        var method = (init && init.method) || (input && input.method) || 'GET';
        if (/\/cart\/add(\.js)?(\?|$)/.test(url) && /post/i.test(method)) {
          var body = (init && init.body) || null;
          // attach to a copy of the chain; never changes what the caller receives
          p.then(function (res) {
            try { if (res && res.ok) idsFromBody(body).forEach(convert); } catch (e) {}
          }).catch(function () {});
        }
      } catch (e) {}
      return p;
    };
    wrapped.__knobsWrapped = true;
    window.fetch = wrapped;
  }

  window.KnobsInsights = { store: store, recordClick: recordClick, convert: convert };
})();
